/**
 * Progressive LOD1 tile loading around the camera (PRD R5 / parent spec
 * §13: "Terrain tiles loaded around camera only"). Reads the manifest
 * that data/config/data_manifest.json publishes (synced into
 * public/data/config/ for dev/build) — never invents a tile list.
 */
import { BufferGeometry, Group, Mesh, MeshStandardMaterial, Object3D } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import type { DataManifest, ManifestTile } from '../core/manifest';
import { tileLocalBounds, tileLocalCentre } from '../core/manifest';
import { applyShadingPresetToMaterial, applyTerrainTint, createTerrainMaterial } from './terrainMaterial';
import { reconcilePendingTiles } from './terrainNormalReconcile';
import { ensureUpwardTerrainNormals } from './terrainWinding';
import { TerrainCollider } from './collider';
import type { QualityTier } from '../core/quality';
import type { ShadingPreset } from '../core/shadingPreset';
import { mapWithConcurrency } from '../core/concurrency';

export interface LoadedTile {
  id: string;
  mesh: Mesh;
}

/** First Mesh found via a depth-first traversal of a loaded glTF scene graph. */
export function firstMesh(root: Object3D): Mesh | null {
  let found: Mesh | null = null;
  root.traverse((obj) => {
    if (!found && (obj as Mesh).isMesh) found = obj as Mesh;
  });
  return found;
}

export function distanceToTileCentre(tile: Pick<ManifestTile, 'bounds_bng'>, x: number, z: number): number {
  const c = tileLocalCentre(tile);
  return Math.hypot(c.x - x, c.z - z);
}

/**
 * GLTFLoader for terrain tiles with the meshopt decoder registered, so
 * EXT_meshopt_compression tiles (the pipeline's opt-in gltfpack step) decode.
 * Plain uncompressed glb still loads through the same loader unchanged, so this
 * is backward compatible with the tiles shipped today.
 */
export function createTileGltfLoader(): GLTFLoader {
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  return loader;
}

export class TileLoader {
  readonly group = new Group();
  readonly collider = new TerrainCollider();
  private readonly manifest: DataManifest;
  private readonly loaded = new Map<string, LoadedTile>();
  private readonly baseUrl: string;
  private readonly gltfLoader = createTileGltfLoader();
  /** Whether terrain tiles cast/receive shadows (off on the low quality tier,
   * issue #17 — full-scene shadows are a top GPU cost on integrated GPUs). */
  private readonly castShadows: boolean;
  /** Max tiles parsed concurrently, to avoid a main-thread parse burst at boot. */
  private readonly loadConcurrency: number;
  /** One shared terrain material for every tile (issue #17: no per-tile
   * materials). Built once at the boot-time quality tier; the noise bake behind
   * the high-tier path is itself shared across all materials. */
  readonly material: MeshStandardMaterial;
  /** The shading preset (issue #106) every newly-attached tile is tinted
   * under. Mutable via setShadingPreset(), which also retints tiles already
   * loaded — this field is what a *new* attachTile() call consults. */
  private preset: ShadingPreset;
  /** Ids of tiles attached since the last {@link reconcileBorderNormals} call,
   * i.e. those whose shared borders may not yet agree with their neighbours'
   * normals (issue #107). Drained on the tile-update tick, never on the render
   * loop. A tile that unloads before it is drained is removed from the set. */
  private readonly pendingReconcile = new Set<string>();

  constructor(
    manifest: DataManifest,
    baseUrl = '/data/terrain_tiles/lod1',
    options: { shadows?: boolean; loadConcurrency?: number; tier?: QualityTier; preset?: ShadingPreset } = {},
  ) {
    this.manifest = manifest;
    this.baseUrl = baseUrl;
    this.castShadows = options.shadows ?? true;
    this.loadConcurrency = options.loadConcurrency ?? 4;
    this.preset = options.preset ?? 'stylised';
    this.material = createTerrainMaterial(options.tier ?? 'high', this.preset);
  }

  get loadedTileIds(): string[] {
    return [...this.loaded.keys()];
  }

  get loadedTileCount(): number {
    return this.loaded.size;
  }

  /** Manifest tiles whose centre is within radiusM of (x, z), nearest first. */
  tilesWithin(x: number, z: number, radiusM: number): ManifestTile[] {
    return this.manifest.tiles
      .map((tile) => ({ tile, d: distanceToTileCentre(tile, x, z) }))
      .filter(({ d }) => d <= radiusM)
      .sort((a, b) => a.d - b.d)
      .map(({ tile }) => tile);
  }

  async loadTile(tile: ManifestTile): Promise<void> {
    if (this.loaded.has(tile.id)) return;
    const fileName = tile.file.split('/').pop();
    if (!fileName) throw new Error(`tile ${tile.id} has no file name in the manifest`);
    const url = `${this.baseUrl}/${fileName}`;
    const gltf = await this.gltfLoader.loadAsync(url);
    const mesh = firstMesh(gltf.scene);
    if (!mesh) throw new Error(`tile ${tile.id} (${url}) has no mesh in its glTF scene`);
    this.attachTile(mesh, tile.id);
  }

  /**
   * Dresses a loaded tile mesh and registers it: vertex-colour base palette,
   * the single shared terrain material, shadow flags, and collider entry.
   * Split out from loadTile so the shared-material invariant is unit-testable
   * without a network/GLTF fetch.
   */
  attachTile(mesh: Mesh, id: string): void {
    // Collider bounds come from the manifest tile, never guessed from the mesh
    // geometry — a tile id absent from the manifest is a reported blocker
    // (hard constraint #7), not a silently indexed guess.
    const tile = this.manifest.tiles.find((t) => t.id === id);
    if (!tile) {
      throw new Error(`tile ${id} is not in the manifest; cannot register collider bounds`);
    }
    // Canonicalise the tile's normals to face up before anything reads them
    // (issue #135): the shipped TIN tiles are wound reversed, so their computed
    // normals point down. Left as-is, Natural-preset lighting reads the signed
    // normal and the tile-border reconcile pass (issue #107) then produces dark
    // tile-edge bands. This reverses the winding when needed so normals face up
    // and the surface is front-facing; positions are untouched, so the collider
    // and LOS are unaffected (hard constraints #1, #6).
    ensureUpwardTerrainNormals(mesh.geometry);
    applyTerrainTint(mesh.geometry, this.preset);
    mesh.material = this.material;
    mesh.castShadow = this.castShadows;
    mesh.receiveShadow = this.castShadows;
    mesh.name = id;
    this.group.add(mesh);
    this.collider.addTile(id, mesh, tileLocalBounds(tile));
    this.loaded.set(id, { id, mesh });
    // Border normals still "see" only this tile's triangles; they are
    // reconciled with neighbours on the next tile-update tick (issue #107),
    // not here — attachTile runs inside the concurrent load loop and a tile's
    // neighbours may not be attached yet.
    this.pendingReconcile.add(id);
  }

  /**
   * Reconciles the vertex normals of tiles attached since the last call with
   * their already-loaded neighbours, so a shared border vertex holds the same
   * normal in every tile that touches it (issue #107). Removes the tile-edge
   * lighting seam the Natural preset (issue #106) otherwise shows, and corrects
   * the border slope tint under either preset. Idempotent and event-driven:
   * call it on the tile-update tick (the same cadence RouteRibbon self-heals
   * on), NEVER on the render loop. A no-op when nothing has attached since the
   * last call, so it is safe to call unconditionally.
   *
   * Only the 'normal' and (via the preset-aware retint) 'color' attributes are
   * touched; `position` stays byte-identical, so the collider and every LOS /
   * ground result are unaffected (hard constraints #1, #6). Returns the number
   * of tiles whose colours were refreshed, for diagnostics/tests.
   */
  reconcileBorderNormals(): number {
    if (this.pendingReconcile.size === 0) return 0;
    const geometryToId = new Map<BufferGeometry, string>();
    const allLoaded: BufferGeometry[] = [];
    for (const { id, mesh } of this.loaded.values()) {
      geometryToId.set(mesh.geometry, id);
      allLoaded.push(mesh.geometry);
    }
    const pending: BufferGeometry[] = [];
    for (const id of this.pendingReconcile) {
      const entry = this.loaded.get(id);
      if (entry) pending.push(entry.mesh.geometry);
    }
    const changed = reconcilePendingTiles(pending, allLoaded);
    for (const geometry of changed) {
      // Refresh through the same preset-aware tint path a fresh attach uses, so
      // the blended border normals feed slope tint identically (issue #106).
      applyTerrainTint(geometry, this.preset);
    }
    this.pendingReconcile.clear();
    return changed.size;
  }

  /** The shading preset every currently- and newly-attached tile renders
   * under. */
  get shadingPreset(): ShadingPreset {
    return this.preset;
  }

  /**
   * Live preset switch (issue #106): flips the one shared material's
   * `flatShading` in place (never a new material — the shared-material
   * invariant, issue #17, holds across a switch) and retints every
   * already-loaded tile's vertex-colour attribute so there is no permanently
   * mixed state between tiles attached before vs. after the switch. A tile
   * attached later picks up the new preset automatically via attachTile's
   * use of `this.preset`. Only touches the 'color' attribute — `position`
   * (and therefore collider results) is untouched.
   *
   * A no-op when the preset is unchanged, so main.ts can call this
   * unconditionally from the HUD click handler without checking first.
   */
  setShadingPreset(preset: ShadingPreset): void {
    if (this.preset === preset) return;
    this.preset = preset;
    applyShadingPresetToMaterial(this.material, preset);
    for (const { mesh } of this.loaded.values()) {
      applyTerrainTint(mesh.geometry, preset);
    }
  }

  unloadTile(id: string): void {
    const entry = this.loaded.get(id);
    if (!entry) return;
    // A tile that unloads before its borders were reconciled needs no
    // reconciliation work (issue #107); drop it from the pending set. Neighbours
    // keep their already-blended normals — cosmetic, and re-converges if the
    // tile reloads.
    this.pendingReconcile.delete(id);
    this.group.remove(entry.mesh);
    this.collider.removeTile(id);
    // Dispose only the tile's own geometry. The material is shared across every
    // tile (issue #17) and owned by this loader, so it must NOT be disposed on
    // per-tile unload — that would break every other live tile. It is released
    // in dispose().
    entry.mesh.geometry.dispose();
    this.loaded.delete(id);
  }

  /** Releases the shared material (and its baked noise texture) plus any
   * still-loaded tile geometry. Call on viewer teardown. */
  dispose(): void {
    for (const id of this.loadedTileIds) this.unloadTile(id);
    this.material.userData.terrainNoiseTexture?.dispose?.();
    this.material.dispose();
  }

  /** Loads tiles within loadRadiusM of (x, z); unloads previously-loaded tiles beyond unloadRadiusM. */
  async updateAround(x: number, z: number, loadRadiusM: number, unloadRadiusM: number): Promise<void> {
    const toLoad = this.tilesWithin(x, z, loadRadiusM);
    // Bounded concurrency + a macrotask yield between tiles (issue #17):
    // parsing every GLB in one microtask burst (especially once tiles are
    // HTTP-cached) freezes the tab for seconds. This keeps the main thread
    // responsive while streaming the AOI in.
    await mapWithConcurrency(toLoad, this.loadConcurrency, (tile) => this.loadTile(tile), { yieldBetween: true });
    for (const id of this.loadedTileIds) {
      const tile = this.manifest.tiles.find((t) => t.id === id);
      if (!tile || distanceToTileCentre(tile, x, z) > unloadRadiusM) this.unloadTile(id);
    }
  }
}
